import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, Input, Self } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { AuthenticationService } from '@flexyoo/auth';
import { EmployeeDto, UserAgreementType } from '@flexyoo/api/models';
import { EmployeeApiService } from '@flexyoo/api/services';
import {
  EmployeeQuillUserAgreementSignNotification,
  IEmployeeSettings,
  QuillSignStatus,
  UserAgreementQuillSignContext,
} from '@flexyoo/employee/models';
import {
  EmployeeSettingsActions,
  quillUserAgreementSignNotificationsActions,
} from '@flexyoo/employee/store';
import { FlexyooLetDirective } from '@flexyoo/shared/directives';
import { blobToText } from '@flexyoo/shared/functions';
import { QuillDataAccessService } from '@flexyoo/shared/services';
import { LoaderComponent } from '@flexyoo/shared/ui';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, catchError, finalize, forkJoin, map, of, switchMap, takeUntil } from 'rxjs';
import { NgOnDestroy } from 'src/app/shared/ng-on-destroy.service';

type SignProcessEmployeeSettings = Pick<IEmployeeSettings, 'currentUserAgreementTypeInSignProcess'>;

@Component({
  selector: 'app-user-agreement-sign',
  templateUrl: './user-agreement-sign.component.html',
  styleUrl: './user-agreement-sign.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, TranslateModule, AsyncPipe, LoaderComponent, FlexyooLetDirective],
  providers: [NgOnDestroy],
})
export class UserAgreementSignComponent {
  public readonly userAgreementType = input.required<UserAgreementType>();
  @Input() public quillSignContext: UserAgreementQuillSignContext = 'cafeteria';

  private readonly authService = inject(AuthenticationService);
  private readonly quillService = inject(QuillDataAccessService);
  private readonly employeeApi = inject(EmployeeApiService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  protected readonly isLoading$ = new BehaviorSubject<boolean>(false);

  protected readonly quillDocumentStatus$ = new BehaviorSubject<QuillSignStatus | ''>('');
  protected readonly quillDocumentId$ = new BehaviorSubject<number | null>(null);

  constructor(@Self() private readonly onDestroy$: NgOnDestroy) {
    this.loadSignState()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(({ employee, employeeSettings }) => {
        this.applySignState(employee, employeeSettings);
      });

    this.actions$
      .pipe(
        takeUntil(this.onDestroy$),
        ofType(quillUserAgreementSignNotificationsActions.received),
        switchMap((action) =>
          this.loadSignState().pipe(
            map(({ employee, employeeSettings }) => ({ action, employee, employeeSettings }))
          )
        )
      )
      .subscribe(({ action, employee, employeeSettings }) => {
        if (!this.shouldHandleNotification(action.notification, employeeSettings)) {
          return;
        }

        this.applySignState(employee, employeeSettings);
        this.processQuillStatus(action.notification.quillStatus);
      });
  }

  private loadSignState() {
    this.store.dispatch(EmployeeSettingsActions.load());

    return forkJoin({
      employee: this.employeeApi.getById(+this.authService.getCurrentUser().EmployeeId),
      employeeSettings: this.employeeApi.getMeSettings().pipe(
        catchError(() => of(null)),
        map(
          (settings): SignProcessEmployeeSettings => ({
            currentUserAgreementTypeInSignProcess:
              settings?.CurrentUserAgreementTypeInSignProcess ?? null,
          })
        )
      ),
    });
  }

  private applySignState(
    employee: EmployeeDto,
    employeeSettings: SignProcessEmployeeSettings | null
  ): void {
    const docId = this.getQuillDocumentId(employee, employeeSettings);
    this.quillDocumentId$.next(docId);

    if (docId) {
      this.updateStatus(docId);
      return;
    }

    this.quillDocumentStatus$.next('');
  }

  protected sign(): void {
    const employeeId = this.authService.getCurrentUser().EmployeeId;
    const userAgreementType = this.userAgreementType();
    if (!employeeId || userAgreementType === null || userAgreementType === undefined) {
      return;
    }

    // Opened synchronously within the click handler so browsers treat it as
    // a user-initiated popup; opening it later from the subscribe callback
    // (after the async API call resolves) gets it silently closed by popup blockers.
    const signWindow = window.open('', '_blank');

    this.isLoading$.next(true);
    this.quillService
      .signUserAgreement(employeeId, userAgreementType)
      .pipe(finalize(() => this.isLoading$.next(false)))
      .subscribe((response) => {
        if (signWindow) {
          signWindow.location.href = response.SignUrl;
        }
        const docId = response.DocumentIds?.[0];
        if (docId) {
          this.quillDocumentId$.next(docId);
          this.updateStatus(docId);
        }
      });
  }

  protected updateStatus(id: number): void {
    this.isLoading$.next(true);
    this.quillService
      .getStatus(id)
      .pipe(
        switchMap((data) => blobToText(new Blob([data]))),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe((res: QuillSignStatus) => this.processQuillStatus(res));
  }

  private getQuillDocumentId(
    employee: EmployeeDto,
    employeeSettings: SignProcessEmployeeSettings | null
  ): number | null {
    if (
      !employee.QuillDocumentId ||
      employeeSettings?.currentUserAgreementTypeInSignProcess === null ||
      employeeSettings?.currentUserAgreementTypeInSignProcess === undefined
    ) {
      return null;
    }

    if (!this.isNotificationForCurrentContext(employeeSettings)) {
      return null;
    }

    return employee.QuillDocumentId;
  }

  private isNotificationForCurrentContext(
    employeeSettings: SignProcessEmployeeSettings | null
  ): boolean {
    const isMobilityContext = this.quillSignContext === 'mobility-budget';
    const isMobilityType =
      employeeSettings?.currentUserAgreementTypeInSignProcess === UserAgreementType.MobilityBudget;

    return isMobilityContext === isMobilityType;
  }

  private shouldHandleNotification(
    notification: EmployeeQuillUserAgreementSignNotification,
    employeeSettings: SignProcessEmployeeSettings | null
  ): boolean {
    const notificationDocId = notification?.quillDocumentId;
    const currentDocId = this.quillDocumentId$.value;
    const quillStatus = notification?.quillStatus?.toUpperCase();
    const isTerminalStatus = quillStatus === 'DECLINED' || quillStatus === 'SIGNED';

    if (currentDocId && notificationDocId && currentDocId === notificationDocId) {
      return true;
    }

    if (isTerminalStatus && currentDocId) {
      return true;
    }

    return this.isNotificationForCurrentContext(employeeSettings);
  }

  private processQuillStatus(status: QuillSignStatus): void {
    const quillStatus = status?.toUpperCase();

    switch (quillStatus) {
      case 'DECLINED': {
        this.quillDocumentId$.next(null);
        this.quillDocumentStatus$.next('');
        break;
      }
      case 'SIGNED': {
        this.quillDocumentStatus$.next(quillStatus);
        location.reload();
        break;
      }
      case 'WAITING_FOR_SIGNATURES': {
        this.quillDocumentStatus$.next(quillStatus);
        break;
      }
    }
  }
}
